import { Component } from "@theme/component";
import {
  CartAddEvent,
  CartErrorEvent,
  CartGroupedSections,
  CartUpdateEvent,
  ThemeEvents,
  VariantUpdateEvent,
} from "@theme/events";
import { fetchConfig, formatCurrency } from "@theme/utilities";

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @property {HTMLElement} [addToCartSpinner] - Loading spinner.
 * @property {HTMLElement} [addToCartPrice] - Optional Endpreis in the button label.
 * @property {HTMLScriptElement} [atcVariantPrices] - JSON map of variant/plan prices.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ["addToCartButton"];

  /** @type {AbortController | undefined} */
  #abort;

  /** @type {Array<{id: number|string, price: number, plans?: Array<{id: number|string, price: number}>}>} */
  #variantPrices = [];

  /** @type {string | null} */
  #currentVariantId = null;

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    // Listen for cart add events to remove loading state
    document.addEventListener(CartAddEvent.eventName, this.#onCartAddDone, { signal });
    document.addEventListener(CartErrorEvent.eventName, this.#onCartAddDone, { signal });

    super.connectedCallback();

    if (this.dataset.showPrice === "true") {
      this.#parseVariantPrices();
      const form = this.closest("product-form-component");
      const section = this.closest(".shopify-section, .product-information, dialog") || document;

      const initialVariantId =
        form?.refs?.variantId?.value ||
        /** @type {HTMLInputElement | null} */ (form?.querySelector('input[name="id"]'))?.value;
      if (initialVariantId) this.#currentVariantId = String(initialVariantId);

      section.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
      form?.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantityChange, { signal });

      const qtyControl = form?.querySelector(
        'quantity-selector-component input[ref="quantityInput"], select[name="quantity"], input[name="quantity"]'
      );
      qtyControl?.addEventListener("input", this.#syncPrice, { signal });
      qtyControl?.addEventListener("change", this.#syncPrice, { signal });

      const vePackControl = form?.querySelector("[data-ve-pack-count]");
      vePackControl?.addEventListener("input", this.#syncPrice, { signal });
      vePackControl?.addEventListener("change", this.#syncPrice, { signal });
      form?.addEventListener("ve-quantity:sync", this.#syncPrice, { signal });

      const sellingPlanInput = form?.querySelector('input.selected-selling-plan-id, input[name="selling_plan"]');
      sellingPlanInput?.addEventListener("change", this.#syncPrice, { signal });
      sellingPlanInput?.addEventListener("input", this.#syncPrice, { signal });
      document.addEventListener("selling-plan:changed", this.#syncPrice, { signal });

      // Spar-Abo mode / plan select live outside the form
      const scope =
        this.closest(".product-information") ||
        this.closest(".quick-add__content") ||
        this.closest("dialog") ||
        document;
      scope.querySelectorAll("[data-we-site-abonnement-mode], [data-we-site-abonnement-plan]").forEach((el) => {
        el.addEventListener("change", this.#syncPrice, { signal });
      });

      this.#syncPrice();
    }
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#abort = undefined;
    super.disconnectedCallback();
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
    this.#syncPrice();
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
    this.#syncPrice();
  }

  /**
   * Adds loading class to the add to cart button.
   */
  addLoading() {
    this.refs.addToCartButton.classList.add("btn--loading");
    this.refs.addToCartSpinner?.classList.remove("hidden");
  }

  /**
   * Removes loading class from the add to cart button.
   */
  removeLoading() {
    this.refs.addToCartButton.classList.remove("btn--loading");
    this.refs.addToCartSpinner?.classList.add("hidden");
  }

  /**
   * Handles cart add completion (success or error).
   * Removes loading state from the button.
   */
  #onCartAddDone = () => {
    this.removeLoading();
  };

  #parseVariantPrices() {
    const script = this.refs.atcVariantPrices || this.querySelector('[ref="atcVariantPrices"]');
    if (!(script instanceof HTMLScriptElement)) return;
    try {
      this.#variantPrices = JSON.parse(script.textContent || "[]");
    } catch {
      this.#variantPrices = [];
    }
  }

  /** @type {EventListener} */
  #onVariantUpdate = (e) => {
    if (!(e instanceof VariantUpdateEvent) && e.type !== ThemeEvents.variantUpdate) return;
    const detail = /** @type {VariantUpdateEvent} */ (e).detail;
    const form = this.closest("product-form-component");
    const productId = form?.dataset.productId;
    if (detail?.data?.productId && productId && String(detail.data.productId) !== String(productId)) {
      return;
    }

    const resource = detail?.resource;
    this.#currentVariantId = resource?.id != null ? String(resource.id) : null;

    // After ProductForm updates disabled state, label, and variant id input
    queueMicrotask(() => this.#syncPrice());
  };

  /** @type {EventListener} */
  #onQuantityChange = (e) => {
    const form = this.closest("product-form-component");
    if (!form?.contains(/** @type {Node} */ (e.target))) return;
    this.#syncPrice();
  };

  #getQuantity() {
    const form = this.closest("product-form-component");
    // VE: ATC Endpreis uses pack count; pack price already includes ve_size × discount
    if (form?.dataset.veEnabled === "true") {
      const packControl = form.querySelector("[data-ve-pack-count]");
      if (packControl instanceof HTMLInputElement || packControl instanceof HTMLSelectElement) {
        const packCount = parseInt(packControl.value, 10);
        return Number.isFinite(packCount) && packCount > 0 ? packCount : 1;
      }
    }
    const control = form?.querySelector(
      'quantity-selector-component input[ref="quantityInput"], select[name="quantity"], input[name="quantity"]'
    );
    if (!(control instanceof HTMLInputElement || control instanceof HTMLSelectElement)) return 1;
    const qty = parseInt(control.value, 10);
    return Number.isFinite(qty) && qty > 0 ? qty : 1;
  }

  #getUnitPriceCents() {
    const form = this.closest("product-form-component");
    const variantId =
      this.#currentVariantId ||
      form?.refs?.variantId?.value ||
      /** @type {HTMLInputElement | null} */ (form?.querySelector('input[name="id"]'))?.value;
    const sellingPlanId = /** @type {HTMLInputElement | null} */ (
      form?.querySelector('input.selected-selling-plan-id, input[name="selling_plan"]')
    )?.value;

    const variantData = this.#variantPrices.find((v) => String(v.id) === String(variantId));
    if (!variantData) return 0;

    let unitCents = Number(variantData.price) || 0;
    if (sellingPlanId) {
      const plan = variantData.plans?.find((p) => String(p.id) === String(sellingPlanId));
      if (plan?.price != null) unitCents = Number(plan.price);
    }

    // Packung: stack Packung % on the current unit price (one-time or selling-plan).
    // Match Shopify/Regios line pricing: apply % per unit (floor discount cents),
    // then multiply by pack size. Rounding the pack total instead causes 1¢ drift
    // (e.g. 584×12×0.8 → €56.06 vs cart 12×€4.68 → €56.16).
    if (form?.dataset.veEnabled === "true") {
      const scope =
        form.closest(".product-information, .shopify-section, dialog, .quick-add__content") || document;
      const radio = /** @type {HTMLInputElement | null} */ (
        scope.querySelector(
          `[data-we-site-packung][data-product-id="${form.dataset.productId}"] input[type="radio"]:checked`
        ) || scope.querySelector("[data-we-site-packung] input[type='radio']:checked")
      );
      const veSize = Number(radio?.dataset.veSize) || Number(form.dataset.veSize) || 1;
      const discountPct = Number(radio?.dataset.discountPct) || Number(form.dataset.veDiscountPct) || 0;
      const size = veSize > 0 ? veSize : 1;
      const discountedUnit =
        discountPct > 0 ? unitCents - Math.floor((unitCents * discountPct) / 100) : unitCents;
      return discountedUnit * size;
    }

    return unitCents;
  }

  #syncPrice = () => {
    if (this.dataset.showPrice !== "true") return;

    const button = this.refs.addToCartButton;
    const priceEl =
      this.refs.addToCartPrice ||
      /** @type {HTMLElement | null} */ (button?.querySelector(".add-to-cart-text__price"));
    const separator = button?.querySelector(".add-to-cart-text__separator");
    if (!button || !priceEl) return;

    const labelEl = button.querySelector(".add-to-cart-text__content");
    const label = labelEl?.textContent?.trim() || button.dataset.addToCartText || "";
    const show = !button.disabled;

    if (separator instanceof HTMLElement) separator.hidden = !show;
    priceEl.hidden = !show;

    if (!show) {
      button.setAttribute("aria-label", label);
      return;
    }

    const total = this.#getUnitPriceCents() * this.#getQuantity();
    const formatted = formatCurrency(total, this.dataset.moneyFormat || undefined);
    priceEl.textContent = formatted;
    button.setAttribute("aria-label", `${label} – ${formatted}`);
  };
}

if (!customElements.get("add-to-cart-component")) {
  customElements.define("add-to-cart-component", AddToCartComponent);
}

function newPackungLineId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ["variantId", "liveRegion"];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest(".shopify-section, dialog, product-card");
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, {
      signal,
    });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Dispatches {@link CartGroupedSections} and returns unique section ids for cart `sections` params.
   *
   * @returns {string[]}
   */
  #gatherGroupedSectionIds() {
    const sections = [];
    document.dispatchEvent(new CartGroupedSections(sections));
    return [...new Set(sections)];
  }

  /**
   * Handles the submit event for the product form.
   *
   * @param {Event} event - The submit event.
   */
  handleSubmit(event) {
    const { addToCartTextError } = this.refs;
    // Stop default behaviour from the browser
    event.preventDefault();

    if (this.#timeout) clearTimeout(this.#timeout);

    // Check if the add to cart button is disabled and do an early return if it is
    if (this.refs.addToCartButtonContainer?.refs.addToCartButton?.getAttribute("disabled") === "true") return;

    // Add loading state to the add to cart button
    this.refs.addToCartButtonContainer?.addLoading();

    // Send the add to cart information to the cart
    const form = this.querySelector("form");

    if (!form) throw new Error("Product form element missing");

    const formData = new FormData(form);
    this.#stampUniqueVeLine(formData);

    const groupedSectionIds = this.#gatherGroupedSectionIds();
    if (groupedSectionIds.length > 0) {
      formData.append("sections", groupedSectionIds.join(","));
    }

    const fetchCfg = fetchConfig("javascript", { body: formData });

    fetch(FoxTheme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: "text/html",
      },
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(form.getAttribute("id") || "", response.message, response.description, response.errors)
          );

          document.dispatchEvent(
            new CustomEvent(ThemeEvents.productAjaxError, {
              detail: { errorMessage: response.description || response.message },
            })
          );

          this.refs.addToCartButtonContainer?.removeLoading();

          if (!addToCartTextError) return;

          // Clear existing timeout to reset the 5s timer
          if (this.#timeout) {
            clearTimeout(this.#timeout);
          }

          // Set error message (replaces any existing message)
          addToCartTextError.textContent = response.description || response.message;
          addToCartTextError.classList.remove("hidden");

          // Announce error for screen readers
          this.#setLiveRegionText(response.message);

          // Hide error message after 5 seconds
          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add("hidden");
            this.#clearLiveRegionText();
          }, 5000);

          // When error occurs (e.g. adding more items than available),
          // backend still adds the max allowed amount to cart.
          // Dispatch CartUpdateEvent with actual count for cart count sync.
          if (response.sections) {
            let actualItemCount = 0;
            let foundCount = false;

            for (const section of Object.values(response.sections)) {
              const tempDiv = document.createElement("div");
              tempDiv.innerHTML = section;

              // Try cart-items ref first, fallback to cart-count bubble
              let itemCountElement = tempDiv.querySelector('[ref="cartItemCount"]');
              if (!itemCountElement) {
                itemCountElement = tempDiv.querySelector(".cart-bubble__text-count");
              }

              if (itemCountElement) {
                actualItemCount = parseInt(itemCountElement.textContent || "0", 10);
                foundCount = true;
                break;
              }
            }
            if (foundCount) {
              this.dispatchEvent(
                new CartUpdateEvent({}, this.id, {
                  itemCount: actualItemCount,
                  source: "product-form-component",
                  productId: this.dataset.productId,
                  sections: response.sections,
                })
              );
            }
          } else {
            // Shopify doesn't return sections on 422 errors
            // Fetch cart.js AND section HTML in parallel for instant morph
            const sectionsData = {};
            const sectionPromises = this.#gatherGroupedSectionIds().map(async (sectionId) => {
              const sectionUrl = `${window.location.pathname.split("?")[0]}?section_id=${sectionId}`;
              const res = await fetch(sectionUrl);
              sectionsData[sectionId] = await res.text();
            });

            const cartPromise = fetch(FoxTheme.routes.cart).then((res) => res.json());

            Promise.all([cartPromise, ...sectionPromises])
              .then(([cart]) => {
                this.dispatchEvent(
                  new CartUpdateEvent(cart, this.id, {
                    itemCount: cart.item_count || 0,
                    source: "product-form-component",
                    productId: this.dataset.productId,
                    sections: Object.keys(sectionsData).length > 0 ? sectionsData : undefined,
                  })
                );
              })
              .catch((error) => {
                console.error("Failed to fetch cart count:", error);
              });
          }

          return;
        } else {
          const id = formData.get("id");

          if (addToCartTextError) {
            addToCartTextError.classList.add("hidden");
            addToCartTextError.removeAttribute("aria-live");
          }

          if (!id) throw new Error("Form ID is required");

          // Add aria-live region to inform screen readers that the item was added
          if (this.refs.addToCartButtonContainer?.refs.addToCartButton) {
            const addedText = FoxTheme.translations.added;
            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, 5000);
          }

          this.dispatchEvent(
            new CartAddEvent(response, id.toString(), {
              source: "product-form-component",
              itemCount: Number(formData.get("quantity")) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
              variantId: id.toString(),
              sections: response.sections,
            })
          );

          document.dispatchEvent(
            new CustomEvent(ThemeEvents.productAjaxAdded, {
              detail: { product: response },
            })
          );
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        // add more thing to do in here if needed.
        // cartPerformance.measureFromEvent("add:user-action", event);
      });
  }

  /**
   * VE packs must not merge with an existing cart line of the same variant.
   * Shopify stacks items that share variant + selling plan + properties;
   * a unique `_packung_line` keeps each ATC as its own line so a leftover
   * (e.g. 13th can) can sit at full price next to a complete VE.
   * @param {FormData} formData
   */
  #stampUniqueVeLine(formData) {
    const veSize = Number(formData.get("properties[_ve_size]"));
    if (!Number.isFinite(veSize) || veSize < 2) return;
    formData.set("properties[_packung_line]", newPackungLineId());
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = "";
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId, addToCartButtonContainer } = this.refs;

    const currentAddToCartButton = addToCartButtonContainer?.refs.addToCartButton;

    if (!currentAddToCartButton) return;

    // Update the button state and text optimistically (always do this, even from cache)
    const variant = event.detail.resource;
    const isAvailable = variant?.available ?? false;

    // Determine correct unavailable state
    // Sold out: Has inventory management (shopify) → inventory = 0
    // Unavailable: No inventory management OR variant is null/undefined → other reasons
    const isSoldOut = !isAvailable && variant && variant.inventory_management === "shopify";

    if (!isAvailable) {
      addToCartButtonContainer.disable();
      this.refs.acceleratedCheckoutButtonContainer?.setAttribute("hidden", "true");
    } else {
      addToCartButtonContainer.enable();
      this.refs.acceleratedCheckoutButtonContainer?.removeAttribute("hidden");
    }

    // Update button text optimistically (works for both available and unavailable)
    // This handles ALL cases: cache hit, JSON fetch, and HTML fallback
    this.#updateButtonTextOptimistic(currentAddToCartButton, isAvailable, isSoldOut);

    // Update the variant ID (always do this)
    if (event.detail.resource?.id) {
      variantId.value = event.detail.resource.id ?? "";
    } else {
      variantId.value = "";
    }
  };

  /**
   * Updates button text optimistically based on variant availability
   * @param {HTMLElement} button - The add to cart button element
   * @param {boolean} isAvailable - Whether the variant is available
   * @param {boolean} isSoldOut - Whether the variant is sold out (has inventory_management = "shopify")
   */
  #updateButtonTextOptimistic(button, isAvailable, isSoldOut = false) {
    if (!button) return;

    const textElement = button.querySelector(".add-to-cart-text__content");
    const iconTextElement = button.querySelector(".btn__icon-text");

    if (!textElement) return;

    // Get text from data attributes (translations from Liquid)
    const availableText = button.dataset.addToCartText || "Add to cart";
    const soldOutText = button.dataset.soldOutText || "Sold out";
    const unavailableText = button.dataset.unavailableText || "Unavailable";

    // Determine correct text based on state
    // - Available: variant.available = true → "Add to cart"
    // - Sold out: variant.available = false + inventory_management = "shopify" → "Sold out"
    // - Unavailable: variant.available = false + no inventory_management → "Unavailable"
    let newText;
    if (isAvailable) {
      newText = availableText;
    } else if (isSoldOut) {
      newText = soldOutText;
    } else {
      newText = unavailableText;
    }

    // Update both text content and icon text (if present)
    textElement.textContent = newText;
    if (iconTextElement) {
      iconTextElement.textContent = newText;
    }
  }

  /**
   * Disable the add to cart button while the UI is updating before #onVariantUpdate is called.
   * Accelerated checkout button is also disabled via its own event listener not exposed to the theme.
   */
  #onVariantSelected = () => {
    this.refs.addToCartButtonContainer?.disable();
  };
}

if (!customElements.get("product-form-component")) {
  customElements.define("product-form-component", ProductFormComponent);
}
